import { canReachBackendApi, resolveApiRequestUrl } from './apiRequestUrl';

/**
 * @returns {{ message: string, code?: string }}
 */
async function friendlyErrorFromResponse(res) {
  let body = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    try {
      body = await res.json();
    } catch {
      /* ignore parse errors */
    }
  }
  const serverMsg = typeof body?.error === 'string' ? body.error : null;
  switch (res.status) {
    case 401:
      return { message: 'Your session could not be verified. Please sign out and sign in again.' };
    case 400:
      return { message: serverMsg || 'The request was invalid. Please try again.' };
    case 502:
      return { message: serverMsg || 'Image generation failed. Please try again.' };
    case 503:
      return { message: serverMsg || 'Image generation is not configured on the server.' };
    default:
      return { message: serverMsg || 'Something went wrong. Please try again.' };
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {{ file: File; prompt: string; quality?: '2K' | '4K' }} params
 * @returns {Promise<
 *   | {
 *       ok: true;
 *       imageDataUrl: string;
 *       contentType: string;
 *       model: string;
 *       promptUsed: string;
 *       outputResolution: string;
 *       aspectRatio: string;
 *     }
 *   | { ok: false; error: string; code?: string }
 * >}
 */
export async function requestNanoBananProImage(user, { file, prompt, quality = '2K' }) {
  if (!user) {
    return { ok: false, error: 'You must be signed in to generate an image.' };
  }
  if (!canReachBackendApi()) {
    return {
      ok: false,
      error: 'The app is not configured to reach the design server. Please contact support.',
    };
  }
  if (!file) {
    return { ok: false, error: 'Please upload an image first.' };
  }
  const trimmedPrompt = typeof prompt === 'string' ? prompt.trim() : '';
  if (!trimmedPrompt) {
    return { ok: false, error: 'Please enter a prompt.' };
  }

  try {
    const idToken = await user.getIdToken(true);
    const formData = new FormData();
    formData.append('image', file, file.name || 'input.jpg');
    formData.append('prompt', trimmedPrompt);
    formData.append('quality', quality === '4K' ? '4K' : '2K');

    const res = await fetch(resolveApiRequestUrl('/api/test/nanobanan-pro'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      body: formData,
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const imageBase64 = typeof body?.imageBase64 === 'string' ? body.imageBase64 : '';
      const contentType =
        typeof body?.contentType === 'string' ? body.contentType : 'image/jpeg';
      if (!imageBase64) {
        return { ok: false, error: 'The server did not return an image.' };
      }
      return {
        ok: true,
        imageDataUrl: `data:${contentType};base64,${imageBase64}`,
        contentType,
        model: typeof body?.model === 'string' ? body.model : '',
        promptUsed: typeof body?.promptUsed === 'string' ? body.promptUsed : trimmedPrompt,
        outputResolution:
          typeof body?.outputResolution === 'string' ? body.outputResolution : quality,
        aspectRatio: typeof body?.aspectRatio === 'string' ? body.aspectRatio : '',
      };
    }

    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, code };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err ?? '');
    const likelyCors =
      typeof message === 'string' &&
      (message.includes('Failed to fetch') || message.includes('NetworkError'));
    return {
      ok: false,
      error:
        process.env.NODE_ENV === 'development' && message
          ? `Could not reach the server. (${message})`
          : likelyCors
            ? 'Could not reach the server. Check your connection and try again.'
            : 'Could not reach the server. Check your connection and try again.',
    };
  }
}
